import Logger from './logger';

/**
 * DisplayGate is a generic, one-shot gate primitive. A slot's render() awaits the promises of the
 * active gates it is not exempt from (see ConcertAds#gatesBlocking); a gate holds those slots back
 * until it opens, after which it never blocks again — slots that render afterwards await an
 * already-resolved promise.
 *
 * It carries no domain knowledge of what it gates. The app-level registry (`app.displayGates`) keys
 * gates by category (e.g. 'firstSlot'); the firstSlot N=1 designation logic lives in the
 * display_control mixin, not here.
 *
 * Lifecycle:
 *   - constructor      creates the (pending) gate promise, registers any onOpen hook, and — when a
 *                      positive timeout is given — starts the backstop timer (unless `deferTimeout`).
 *   - startTimeout()   start a deferred backstop countdown; idempotent, first caller wins.
 *   - restartTimeout() restart the countdown from now (re-anchor to a later event).
 *   - pauseTimeout()   hold the backstop: stop any running countdown, and make start/restart no-ops
 *                      until resumeTimeout(). The gate itself stays pending.
 *   - resumeTimeout()  lift the hold and start a fresh countdown from now.
 *   - runOnOpen(cb)    register a callback to fire synchronously the instant the gate opens.
 *   - open(reason)     open exactly once (via an explicit release or the backstop timer).
 *
 * When the backstop expires the owner can veto it (`extendOnTimeout`) and keep the gate pending for
 * longer. That has to happen at expiry: once a gate has opened its waiters are released and it cannot
 * be closed again, so a late restart is too late to hold anything back.
 */
export default class DisplayGate {
  /**
   * @param {string}   key                Registry category this gate represents.
   * @param {Object}   [options]
   * @param {number}   [options.timeout]  Backstop (ms). > 0 enables it; 0 disables it.
   * @param {Function} [options.onOpen]   Callback run synchronously the instant the gate opens.
   * @param {boolean}  [options.deferTimeout]  Keep the `timeout` but do NOT start the countdown yet —
   *   the owner starts it later with {@link DisplayGate#startTimeout} / {@link DisplayGate#restartTimeout}
   *   (e.g. once the thing the gate waits on is known to be in play). Until then the gate has no
   *   backstop. Default false: the countdown starts at construction.
   * @param {Function} [options.extendOnTimeout]  Asked each time the backstop expires, before the gate
   *   opens. Return a positive number of ms to keep the gate pending and re-arm the countdown for that
   *   long; any other return value (or a throw) lets the gate open with reason 'timeout' as usual.
   *   For an owner that knows what the gate waits on is still on its way when the clock runs out.
   *   Receives the number of extensions already granted on this gate, so the owner can bound how long
   *   it holds (this primitive imposes no limit of its own — an owner that always returns a duration
   *   holds the gate forever).
   */
  constructor(key, { timeout = 0, onOpen, deferTimeout = false, extendOnTimeout } = {}) {
    this.key = key;
    this._opened = false; // idempotent open flag
    this._timeout = timeout; // configured backstop duration (ms); the default for start/restartTimeout
    this._timer = null; // backstop timer handle
    this._paused = false; // backstop held: start/restart are no-ops until resumeTimeout()
    this._onOpen = []; // callbacks run synchronously the instant the gate opens, before it resolves
    this._extendOnTimeout = extendOnTimeout; // optional veto, asked when the backstop expires
    this._extensions = 0; // how many times that veto has granted more time

    this._promise = new Promise(resolve => {
      this._resolve = resolve;
    });

    if (typeof onOpen === 'function') {
      this._onOpen.push(onOpen);
    }

    if (!deferTimeout) {
      this.startTimeout();
    }
  }

  /**
   * Start the backstop countdown. Idempotent: a no-op when the gate is already open, paused, a
   * countdown is already running, or the duration is not positive. The first caller wins, so several
   * independent triggers can each call this and only the earliest starts the clock.
   *
   * @param  {number}  [timeout]  Duration (ms); defaults to the gate's configured `timeout`.
   * @return {boolean} whether this call started the countdown.
   */
  startTimeout(timeout = this._timeout) {
    if (this._opened || this._paused || this._timer || !(timeout > 0)) return false;
    this._armTimer(timeout);
    return true;
  }

  /**
   * Restart the backstop countdown from now, replacing any running one (or starting it if none is).
   * For re-anchoring the clock to a later, more precise event — unlike {@link DisplayGate#startTimeout}
   * this always wins. A no-op once the gate is open, while it is paused, or when the duration is not
   * positive.
   *
   * @param  {number}  [timeout]  Duration (ms); defaults to the gate's configured `timeout`.
   * @return {boolean} whether a countdown was (re)started.
   */
  restartTimeout(timeout = this._timeout) {
    if (this._opened || this._paused || !(timeout > 0)) return false;
    if (this._timer) clearTimeout(this._timer);
    this._armTimer(timeout);
    return true;
  }

  /**
   * Schedule the backstop to expire `duration` ms from now. Every countdown — start, restart, resume and
   * the extension granted by `extendOnTimeout` — goes through here, so they all expire the same way.
   * @param {number} duration ms
   */
  _armTimer(duration) {
    this._timer = setTimeout(() => this._expire(), duration);
  }

  /**
   * The backstop ran out. Open the gate — unless the owner's `extendOnTimeout` asks for more time, in
   * which case stay pending and count down again. Each expiry asks afresh, and the owner is told how many
   * extensions it has already been granted, so bounding the hold is its decision; an explicit open() or
   * pauseTimeout() still ends or suspends it.
   */
  _expire() {
    this._timer = null;

    const extension = this._extension();
    if (extension > 0) {
      this._extensions += 1;
      Logger.log(`[DisplayGate] '${this.key}' backstop expired but is held for ${extension}ms more`);
      this._armTimer(extension);
      return;
    }

    this.open('timeout');
  }

  /**
   * Ask `extendOnTimeout` how much longer to hold, telling it how many extensions it has already had. A
   * missing hook, a non-positive / non-numeric answer, or a throw all mean "don't": the backstop exists so
   * the page never waits forever, so a failing veto must fail open.
   * @return {number} ms to hold, or 0 to open now.
   */
  _extension() {
    if (typeof this._extendOnTimeout !== 'function') return 0;

    try {
      const extension = this._extendOnTimeout(this._extensions);
      return typeof extension === 'number' && extension > 0 ? extension : 0;
    } catch (err) {
      Logger.warn(`[DisplayGate] extendOnTimeout hook threw for '${this.key}'; opening anyway`, {
        error: err && err.message ? err.message : String(err),
      });
      return 0;
    }
  }

  /**
   * Hold the backstop: stop any running countdown and make {@link DisplayGate#startTimeout} /
   * {@link DisplayGate#restartTimeout} no-ops until {@link DisplayGate#resumeTimeout}. The gate stays
   * pending and can still be opened explicitly. Idempotent, and valid on a gate whose countdown was
   * never started, so an owner can hold a gate before its countdown begins.
   *
   * @return {boolean} whether the gate is now held (false once it has opened).
   */
  pauseTimeout() {
    if (this._opened) return false;
    if (this._timer) {
      clearTimeout(this._timer);
      this._timer = null;
    }
    this._paused = true;
    return true;
  }

  /**
   * Lift a {@link DisplayGate#pauseTimeout} hold and start a FRESH countdown from now — the elapsed time
   * is not carried over, so the gate gets its whole window after whatever held it. A no-op if the gate
   * is open or was not paused.
   *
   * @param  {number}  [timeout]  Duration (ms); defaults to the gate's configured `timeout`.
   * @return {boolean} whether a countdown was started (false when the duration is not positive).
   */
  resumeTimeout(timeout = this._timeout) {
    if (this._opened || !this._paused) return false;
    this._paused = false;
    return this.restartTimeout(timeout);
  }

  /**
   * Whether the backstop is currently held by {@link DisplayGate#pauseTimeout}.
   * @return {boolean}
   */
  get paused() {
    return this._paused;
  }

  /**
   * The promise slots await in render() while this gate blocks. Resolves once, when the gate opens.
   * @return {Promise}
   */
  get promise() {
    return this._promise;
  }

  /**
   * Whether the gate has already opened. A gate that has opened via the backstop timer is left in
   * the registry (only an explicit release deletes it), so callers that must distinguish "armed and
   * still blocking" from "already opened" read this rather than the map's presence.
   * @return {boolean}
   */
  get opened() {
    return this._opened;
  }

  /**
   * Register a callback to run the instant the gate opens. It fires SYNCHRONOUSLY inside `open()` —
   * before the gate promise resolves — so anything it does (e.g. force-rendering out-of-page slots)
   * is ordered ahead of the general pack unblocked by the resolve. If the gate is already open, runs
   * now.
   * @param {Function} cb
   */
  runOnOpen(cb) {
    if (typeof cb !== 'function') return;
    if (this._opened) cb();
    else this._onOpen.push(cb);
  }

  /**
   * Open the gate exactly once. All open paths (an explicit release, the backstop timer) funnel here.
   * @param {string} reason for logging
   */
  open(reason) {
    if (this._opened) return;
    this._opened = true;

    if (this._timer) {
      clearTimeout(this._timer);
      this._timer = null;
    }

    // Run onOpen hooks BEFORE resolving so they act first — the resolve below releases every slot
    // awaiting the gate, and their ad.show() microtasks would otherwise be queued ahead of anything
    // scheduled after the resolve.
    //
    // Each hook is isolated in try/catch: this is the gate's one and only open, so a hook that throws
    // (e.g. the out-of-page sweep hitting a torn-down slot) must not skip the resolve below — that
    // would leave the gate promise pending forever and hang every slot awaiting it.
    this._onOpen.forEach(cb => {
      try {
        cb();
      } catch (err) {
        Logger.warn(`[DisplayGate] onOpen hook threw for '${this.key}' (${reason}); opening anyway`, {
          error: err && err.message ? err.message : String(err),
        });
      }
    });
    this._onOpen = [];

    if (this._resolve) this._resolve();
    Logger.log(`[DisplayGate] '${this.key}' opened (${reason})`);
  }
}
